import {
  CHINA_PAYMENT_MIN_TOTAL_RM_CENTS,
  type ChargeDraft,
  type ChargeDraftProblem,
  type ChargeLimits,
  type ChargeStep,
  type ChinaPaymentFees,
  type ChinaPaymentType,
  type PayToAccount,
  type PercentFeeRule,
  type Quote,
  type RateCard,
  type RateTier,
} from './china-payment.js';
import { toIsoTimestamp } from './packages-wire.js';

/**
 * `currencyRate` (`currency=RMB`) → the China Payment rate card (MYP-59, MYP-111).
 *
 * Live (2026-10-02) the reply is `{code: "1", row: {…24 fields…}}`. The ones read here:
 *
 * - `rmltamt1`–`3` — where tiers 2, 3 and 4 start, in **RM** (3000 / 9999 / 90000).
 * - `rmrate1`–`4` — the rate in each tier, ¥ per RM 1 (1.591 / 1.596 / 1.601 / 1.601).
 * - `alipayfee` / `alipayleast` / `servicesfee` — top-up: 3%, or RM 10 under ¥500.
 * - `alipayfee2` / `alipayleast2` / `servicesfee2` — proxy pay: 5%, or RM 20 under ¥500.
 * - `transfee` — ¥15 per bank transfer. `servicescurrency` (`RM`) is the flat fees' currency.
 * - `status` — `Y` when the service is on.
 *
 * `rmbltamt*` / `rmbrate*` are all zero live and unused. A currency with no table answers
 * `row: null`, which reads as no card at all.
 *
 * Note the fee settings agree with the old website, **not** with `alipay_rechange`'s quote (which
 * charges top-ups 0% from ¥500, and proxy pay 3%) — China Payment API issues, item 2.
 */

type Row = Readonly<Record<string, unknown>>;

function text(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** A non-negative decimal, as text, or undefined. */
function decimal(value: unknown): string | undefined {
  const raw = text(value);
  return raw !== undefined && /^\d+(\.\d+)?$/.test(raw) ? raw : undefined;
}

/** `"3000.00"` → 300000. */
function cents(value: unknown): number | undefined {
  const raw = decimal(value);
  return raw === undefined ? undefined : Math.round(Number(raw) * 100);
}

/** A rate the customer can be shown: a positive decimal. `"0"` (an unset tier) is not one. */
function rate(value: unknown): string | undefined {
  const raw = decimal(value);
  return raw !== undefined && Number(raw) > 0 ? raw : undefined;
}

function percentRule(
  row: Row,
  percentKey: string,
  belowKey: string,
  flatKey: string,
): PercentFeeRule | undefined {
  const percent = decimal(row[percentKey]);
  const flatBelowRmbCents = cents(row[belowKey]);
  const flatFeeCents = cents(row[flatKey]);
  if (percent === undefined || flatBelowRmbCents === undefined || flatFeeCents === undefined) {
    return undefined;
  }
  return {
    percent: Number(percent),
    flatBelowRmbCents,
    flatFeeCents,
    flatFeeCurrency: text(row['servicescurrency']) ?? 'RM',
  };
}

/**
 * The tiers, lowest first. Tier *k* runs from limit *k−1* (0 for the first) to limit *k*; the
 * fourth has no upper limit. Reading stops at the first tier without a usable rate — a bounded
 * last row is shown rather than a rate the vendor never set — and a missing or out-of-order limit
 * makes the tier before it the open-ended last one.
 */
function tiersFrom(row: Row): RateTier[] {
  const limits = [cents(row['rmltamt1']), cents(row['rmltamt2']), cents(row['rmltamt3'])];
  const tiers: RateTier[] = [];
  let from = 0;

  for (let k = 0; k < 4; k += 1) {
    const value = rate(row[`rmrate${k + 1}`]);
    if (value === undefined) break;

    const limit = k < 3 ? limits[k] : undefined;
    const bounded = limit !== undefined && limit > from;
    tiers.push(
      bounded
        ? { fromRmCents: from, toRmCents: limit, rate: value }
        : { fromRmCents: from, rate: value },
    );
    if (!bounded) break;
    from = limit;
  }

  // Neighbours with the same rate read as one row: "RM 9,999 and above · 1.601", not two.
  return tiers.reduce<RateTier[]>((merged, tier) => {
    const previous = merged[merged.length - 1];
    if (previous === undefined || Number(previous.rate) !== Number(tier.rate)) {
      merged.push(tier);
    } else {
      merged[merged.length - 1] =
        tier.toRmCents === undefined
          ? { fromRmCents: previous.fromRmCents, rate: previous.rate }
          : { fromRmCents: previous.fromRmCents, toRmCents: tier.toRmCents, rate: previous.rate };
    }
    return merged;
  }, []);
}

export function normaliseRateCard(row: unknown): RateCard | undefined {
  if (typeof row !== 'object' || row === null || Array.isArray(row)) return undefined;
  const record = row as Row;

  const tiers = tiersFrom(record);
  if (tiers.length === 0) return undefined;

  const topUp = percentRule(record, 'alipayfee', 'alipayleast', 'servicesfee');
  const proxyPay = percentRule(record, 'alipayfee2', 'alipayleast2', 'servicesfee2');
  const bankTransferRmbCents = cents(record['transfee']);
  const fees: ChinaPaymentFees = {
    ...(topUp ? { topUp } : {}),
    ...(proxyPay ? { proxyPay } : {}),
    ...(bankTransferRmbCents !== undefined ? { bankTransferRmbCents } : {}),
  };

  const status = text(record['status'])?.toUpperCase();
  return { tiers, fees, active: status === undefined || status === 'Y' };
}

/**
 * What the customer typed in the amount (¥) box → fen, or undefined when it is not an amount the
 * app will ask a quote for: a positive number with at most two decimals. The API itself takes
 * ¥50.5 and even negative amounts (China Payment API issues, item 6), so the check is here.
 */
export function parseRmbAmount(input: string): number | undefined {
  const raw = input.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) return undefined;
  const fen = Math.round(Number(raw) * 100);
  return fen > 0 && Number.isSafeInteger(fen) ? fen : undefined;
}

/** Fen → the `amt` the quote is asked with: yuan, always two decimals (`50000` → `"500.00"`). */
export function rmbAmountParam(rmbCents: number): string {
  return `${Math.floor(rmbCents / 100)}.${String(rmbCents % 100).padStart(2, '0')}`;
}

/** A server amount (`72.8536`, `"10.00"`, `0`) → cents, rounded for display. */
function amountCents(value: unknown): number | undefined {
  const raw = decimal(value);
  return raw === undefined ? undefined : Math.round(Number(raw) * 100);
}

/**
 * `alipay_rechange`'s reply → a `Quote` (MYP-60, MYP-108), or undefined when it lacks a rate or a
 * total. Shapes, live 2026-10-02 (all asked from RMB, `currency=RMB`):
 *
 * - Top-up (`ALIPAYCHARGE` / `WXCHARGE`), ¥100: `{rate: "1.591", servicefee: "10.00", rmpay:
 *   null, sumpays: 72.8536}` — the fee in RM. From ¥500 the fee is `0`.
 * - Proxy pay (`ALIPAY`), ¥500: `{servicefee: 9.428, sumpays: 323.6958, sumpays2: ""}` — RM.
 * - Bank transfer (`BANKTRANS`), ¥500: `{servicefee: "15.00", rmpay: 314.2678, sumpays:
 *   323.6958}` — the fee in **RMB**, and `sumpays` is (¥500 + ¥15) ÷ rate, as the vendor
 *   confirmed (MYP-64). It is shown as sent, never re-added.
 */
export function normaliseQuote(
  type: ChinaPaymentType,
  rmbCents: number,
  body: unknown,
): Quote | undefined {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return undefined;
  const record = body as Row;

  const quoted = rate(record['rate']);
  const totalRmCents = amountCents(record['sumpays']);
  if (quoted === undefined || totalRmCents === undefined || totalRmCents <= 0) return undefined;

  const rmAmountCents = type === 'BANKTRANS' ? amountCents(record['rmpay']) : undefined;
  return {
    type,
    rmbCents,
    rate: quoted,
    serviceFeeCents: amountCents(record['servicefee']) ?? 0,
    serviceFeeCurrency: type === 'BANKTRANS' ? 'RMB' : 'RM',
    totalRmCents,
    ...(rmAmountCents !== undefined ? { rmAmountCents } : {}),
  };
}

/** Where the old website's top-up fee turns from RM 10 into 3%: ¥500. */
const OLD_SITE_TOP_UP_FEE_FROM_RMB_CENTS = 50_000;

/**
 * A top-up quote with the old website's fee (MYP-109): from ¥500 the API (`alipay_rechange`)
 * charges top-ups nothing, where the old website — and the rate card (`currencyRate`) — charge 3%
 * (China Payment API issues, #27). Checked on the old site, 22 Oct 2026: ¥1,111 at 1.575 → RM
 * 705.40 + RM 21.16 = **RM 726.56**; the API says RM 705.40.
 *
 * So for an Alipay / WeChat top-up at or above the rule's threshold that came back with no fee,
 * the fee is worked out here from the rule: RM amount = ¥ ÷ the quote's rate, rounded to cents;
 * fee = `percent`% of that, rounded to cents; total = the two added, as the old site shows them.
 * Anything else — another type, a smaller amount, or a quote that already carries a fee (the
 * vendor fixed it) — is returned as it came. Undefined when the fee is needed but the rate card
 * gave no rule: the total cannot be known then, and none is shown.
 */
export function withTopUpFee(quote: Quote, rule: PercentFeeRule | undefined): Quote | undefined {
  const topUp = quote.type === 'ALIPAYCHARGE' || quote.type === 'WXCHARGE';
  if (!topUp || quote.serviceFeeCents > 0) return quote;
  if (rule !== undefined && quote.rmbCents < rule.flatBelowRmbCents) return quote;
  if (rule === undefined)
    return quote.rmbCents < OLD_SITE_TOP_UP_FEE_FROM_RMB_CENTS ? quote : undefined;
  const rate = Number(quote.rate);
  if (!(rate > 0)) return undefined;
  const rmCents = Math.round(quote.rmbCents / rate);
  const feeCents = Math.round((rmCents * rule.percent) / 100);
  return {
    ...quote,
    serviceFeeCents: feeCents,
    serviceFeeCurrency: 'RM',
    totalRmCents: rmCents + feeCents,
  };
}

/**
 * How the brands on the live list are spelt — the vendor sends "PUBLIC BANK", "MAYBANK", "Duitnow"
 * and "Touch N Go". Only known names are respelt: a blanket title-case would turn "CIMB" into
 * "Cimb".
 */
const BRAND_SPELLING: Readonly<Record<string, string>> = {
  'PUBLIC BANK': 'Public Bank',
  MAYBANK: 'Maybank',
  DUITNOW: 'DuitNow',
  'TOUCH N GO': "Touch 'n Go",
  "TOUCH 'N GO": "Touch 'n Go",
};

/**
 * "PUBLIC BANK - TOP UP FOR ALIPAY" → "Public Bank". The suffix is dropped because the same
 * accounts are shown for proxy pay and bank transfer too, where "for Alipay" would mislead; a
 * known brand gets its usual spelling, and anything else is left as sent.
 */
export function payToAccountName(raw: string): string {
  const base = raw.replace(/\s*-\s*TOP\s*UP\s+FOR\b.*$/i, '').trim() || raw.trim();
  return BRAND_SPELLING[base.toUpperCase().replace(/\s+/g, ' ')] ?? base;
}

/**
 * `getBankList` rows (`accounttype=ALIPAY`) → the accounts to pay into (MYP-61, MYP-110), in the
 * vendor's order. Live (2026-10-02):
 *
 * - `{id: "9", name: "PUBLIC BANK - TOP UP FOR ALIPAY", account: "3170549908", accname: "MYPOST
 *   ONLINE SDN BHD", accimg: null, imgurl: "/Public/Uploads/…png", status: "Y"}` — and Maybank
 *   (id 10) alike, its logo missing from the server.
 * - `{id: "18", name: "Duitnow", account: "MYPOST ONLINE SDN BHD", accimg: "/Public/Uploads/
 *   FixedImg/…jpg", …}` — and Touch 'n Go (id 19) with the same `accimg`: a DuitNow QR poster any
 *   bank app or e-wallet can scan. `account` holds a name here, so there is no number to copy.
 *
 * A row the vendor has switched off (`status` other than `Y`) is left out; one with no id or name
 * is unusable.
 */
export function normalisePayToAccounts(rows: readonly unknown[]): readonly PayToAccount[] {
  const accounts: PayToAccount[] = [];
  for (const row of rows) {
    if (typeof row !== 'object' || row === null) continue;
    const record = row as Row;

    const id = text(record['id']);
    const rawName = text(record['name']);
    const status = text(record['status'])?.toUpperCase();
    if (id === undefined || rawName === undefined || (status !== undefined && status !== 'Y')) {
      continue;
    }

    const account = text(record['account']);
    const accountNumber =
      account !== undefined && /^\d[\d\s-]{4,}\d$/.test(account) ? account : undefined;
    const accountName = text(record['accname']);
    const qrPath = text(record['accimg']);
    const logoPath = text(record['imgurl']);

    accounts.push({
      id,
      name: payToAccountName(rawName),
      ...(accountNumber ? { accountNumber } : {}),
      ...(accountName ? { accountName } : {}),
      ...(qrPath ? { qrPath } : {}),
      ...(logoPath ? { logoPath } : {}),
    });
  }
  return accounts;
}

/** The amount field `ChargeResult` reads for each type (API document, p.37). */
const AMOUNT_FIELD: Readonly<Record<ChinaPaymentType, string>> = {
  ALIPAYCHARGE: 'alipay_recharge_amt',
  WXCHARGE: 'alipay_recharge_amt',
  ALIPAY: 'alipay_paid_rmb',
  BANKTRANS: 'bank_rmb',
};

/** Malaysia is UTC+8 all year (no daylight saving). */
const MALAYSIA_OFFSET_MS = 8 * 60 * 60 * 1000;

/**
 * A moment → the vendor's `YYYY-MM-DD HH:MM:SS` in Malaysia time, whatever the reader's own clock
 * zone. Used for `ChargeResult`'s `date`: the old website asks for no payment time, so the request
 * is dated when it is sent (MYP-109).
 */
export function toVendorDateTime(moment: Date): string {
  const local = new Date(moment.getTime() + MALAYSIA_OFFSET_MS);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())} ` +
    `${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}:${pad(local.getUTCSeconds())}`
  );
}

/**
 * Step 1 — what stops the amount from going on (MYP-109): no valid amount; above what the app
 * allows for the type (`limits.maxRmbCents`, where the vendor's quote is wrong); no quote for it
 * yet; or a total under the old website's RM 100 minimum.
 */
export function chargeAmountProblems(
  draft: Pick<ChargeDraft, 'rmbCents' | 'totalRmCents'>,
  limits: ChargeLimits = {},
): readonly ChargeDraftProblem[] {
  if (draft.rmbCents === undefined || draft.rmbCents <= 0) return ['amount'];
  if (limits.maxRmbCents !== undefined && draft.rmbCents > limits.maxRmbCents) return ['overLimit'];
  if (draft.totalRmCents === undefined) return ['quote'];
  if (draft.totalRmCents < CHINA_PAYMENT_MIN_TOTAL_RM_CENTS) return ['minimum'];
  return [];
}

/**
 * Step 2 — the customer's details, in the old website's order: name, ID number, phone, then the
 * account the request is for (top-ups, proxy pay) or the China recipient (bank transfer; the
 * branch is optional). All of them are required on the old website.
 */
export function chargeDetailsProblems(draft: ChargeDraft): readonly ChargeDraftProblem[] {
  const problems: ChargeDraftProblem[] = [];
  const blank = (value: string) => value.trim() === '';

  if (blank(draft.name)) problems.push('name');
  if (blank(draft.idNumber)) problems.push('idNumber');
  if (blank(draft.phone)) problems.push('phone');
  if (draft.type === 'BANKTRANS') {
    if (blank(draft.recipientName)) problems.push('recipientName');
    if (blank(draft.recipientCardNumber)) problems.push('recipientCardNumber');
    if (blank(draft.recipientBank)) problems.push('recipientBank');
  } else if (blank(draft.payerAccount)) {
    problems.push('payerAccount');
  }
  return problems;
}

/** Step 3 — the MyPoz account paid into, and the receipt. */
export function chargePaymentProblems(draft: ChargeDraft): readonly ChargeDraftProblem[] {
  const problems: ChargeDraftProblem[] = [];
  if (!draft.bankId) problems.push('bank');
  if (!draft.receiptPath) problems.push('receipt');
  return problems;
}

/** Which step asks for each problem — where the form sends the customer to fix it. */
export function chargeProblemStep(problem: ChargeDraftProblem): ChargeStep {
  switch (problem) {
    case 'amount':
    case 'overLimit':
    case 'quote':
    case 'minimum':
      return 'amount';
    case 'bank':
    case 'receipt':
      return 'payment';
    default:
      return 'details';
  }
}

/**
 * Everything that still stops a China Payment request from being sent (MYP-62, MYP-109), step by
 * step, in the order the form asks for it. Empty means ready.
 */
export function chargeDraftProblems(
  draft: ChargeDraft,
  limits: ChargeLimits = {},
): readonly ChargeDraftProblem[] {
  return [
    ...chargeAmountProblems(draft, limits),
    ...chargeDetailsProblems(draft),
    ...chargePaymentProblems(draft),
  ];
}

/**
 * A ready draft → the `ChargeResult` parameters (MYP-62, MYP-109), or undefined while
 * `chargeDraftProblems` still finds something missing.
 *
 * - Always `currency=RMB`: anything else has no rate table (MYP-64).
 * - The amount goes in the field its type reads (`alipay_recharge_amt` / `alipay_paid_rmb` /
 *   `bank_rmb`), in yuan with two decimals. The server works out the RM total itself.
 * - `date` is `now` in the vendor's `YYYY-MM-DD HH:MM:SS`, Malaysia time.
 * - Every key is listed here by name: there is no way for `tbpw` — or anything else not on this
 *   list — to reach the request. The QR picture (`qrcodefile`, which the old website sends) is
 *   not sent until the vendor confirms the API takes it.
 * - Optional fields are left out when empty rather than sent blank.
 */
export function toChargeResultParams(
  draft: ChargeDraft,
  now: Date,
  limits: ChargeLimits = {},
): Readonly<Record<string, string>> | undefined {
  if (chargeDraftProblems(draft, limits).length > 0) return undefined;
  if (draft.rmbCents === undefined || !draft.bankId || !draft.receiptPath) return undefined;

  const params: Record<string, string> = {
    type: draft.type,
    currency: 'RMB',
    [AMOUNT_FIELD[draft.type]]: rmbAmountParam(draft.rmbCents),
    engname: draft.name.trim(),
    idno1: draft.idNumber.trim(),
    telephone: draft.phone.trim(),
    bankid: draft.bankId,
    date: toVendorDateTime(now),
    upfile: draft.receiptPath,
  };
  const optional = (key: string, value: string) => {
    if (value.trim() !== '') params[key] = value.trim();
  };
  if (draft.type === 'BANKTRANS') {
    optional('account', draft.recipientName);
    optional('cardnumber', draft.recipientCardNumber);
    optional('bankname', draft.recipientBank);
    optional('branch', draft.recipientBranch);
  } else {
    optional('tbac', draft.payerAccount);
  }
  if (draft.type === 'ALIPAY') optional('sellername', draft.sellerName);
  optional('remark', draft.remark);
  return params;
}

/** What the history row of a just-sent request is found by. */
export interface SentChargeQuery {
  readonly type: ChinaPaymentType;
  readonly rmbCents: number;
  /** When the request was sent; rows submitted more than a few minutes before it are ignored. */
  readonly since: Date;
  /** The receipt sent with it (`upfile`): every upload gets its own file, so it marks the row. */
  readonly receiptPath?: string;
  /** Request numbers already known to be other requests — say, the one sent just before. */
  readonly exclude?: readonly string[];
}

/** How far before `since` a row may be dated and still count — for a server clock behind ours. */
const SENT_CHARGE_SLACK_MS = 5 * 60 * 1000;

/** `/Public/Uploads/2026-10-22/r123.png` → `r123.png`: compared by name, whatever the folder form. */
function fileName(path: string | undefined): string | undefined {
  const name = path?.split(/[\\/]/).pop()?.trim();
  return name ? name.toLowerCase() : undefined;
}

/**
 * After a `ChargeResult` call whose answer was lost (MYP-109): page 1 of `getalipaylist` →
 * the request number of the row that call created, or undefined when there is none.
 *
 * A row matches when it has the same type and RMB amount, was submitted (`chargetime`, Malaysia
 * time) no earlier than a few minutes before the call, and is not one of `exclude`. Two requests
 * of the same type and amount in a few minutes are common (a second ¥160 top-up, or one made on the
 * old website or in another tab), so when the request carried a receipt — every request from the
 * app does — the row's file (`imgsrc`) must be that one; a row with no receipt, or another, is not
 * a match (review of #56). The newest match wins: page 1 is newest first.
 *
 * Only `type`, `amount`, `chargetime`, `imgsrc` and `orderno` are read — the rows also carry the
 * customer's ID number and passwords (MYP-63), which are never touched here. Unsure means
 * undefined: the customer is then told it couldn't be confirmed, never that it went in.
 */
export function findSentCharge(
  body: unknown,
  query: SentChargeQuery,
): { reference?: string } | undefined {
  const data =
    typeof body === 'object' && body !== null && !Array.isArray(body)
      ? (body as Row)['data']
      : undefined;
  const list = typeof data === 'object' && data !== null ? (data as Row)['list'] : undefined;
  const rows: unknown[] = Array.isArray(list)
    ? list
    : typeof list === 'object' && list !== null
      ? Object.values(list)
      : [];

  const earliest = query.since.getTime() - SENT_CHARGE_SLACK_MS;
  const receipt = fileName(query.receiptPath);
  for (const raw of rows) {
    if (typeof raw !== 'object' || raw === null) continue;
    const row = raw as Row;
    if (text(row['type'])?.toUpperCase() !== query.type) continue;
    if (amountCents(row['amount']) !== query.rmbCents) continue;
    const at = toIsoTimestamp(row['chargetime']);
    if (at === undefined || Date.parse(at) < earliest) continue;
    const reference = text(row['orderno']);
    if (reference !== undefined && query.exclude?.includes(reference)) continue;
    // With a receipt to go by, only the row carrying that receipt is this request.
    if (receipt !== undefined && fileName(text(row['imgsrc'])) !== receipt) continue;
    return reference ? { reference } : {};
  }
  return undefined;
}

/**
 * `ChargeResult`'s reply → the request's reference number, shown to the customer. The document's
 * sample is `{code: 1, orderno: "CH202607240001", msg: "充值成功"}`; undefined when none came back.
 */
export function chargeOrderNumber(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  return text((body as Row)['orderno']);
}
